import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  ClipboardList,
  Gamepad2,
  Gift,
  MapPin,
  ShieldCheck,
  Smartphone,
  Users,
} from 'lucide-react';

export type Product = {
  id: string;
  name: string;
  tagline: string;
  pitch: string;
  description: string;
  features: { icon: LucideIcon; title: string; description: string }[];
  audience: string;
};

export const products: Product[] = [
  {
    id: 'apsales',
    name: 'ApSales',
    tagline: 'Field sales management for modern trade teams',
    pitch:
      'A complete field sales platform pairing a web admin portal with native iOS and Android apps — built for promoters, merchandisers, and sales managers running outlet-based campaigns.',
    description:
      'ApSales digitizes every outlet visit from check-in to reporting. Field staff capture stock, pricing, sales volumes, and conversion data on the go; managers get real-time dashboards, pricing intelligence, and attendance visibility across teams, territories, and projects.',
    features: [
      {
        icon: Smartphone,
        title: 'Native mobile field app',
        description:
          'iOS and Android apps for promoters on the ground — project selection, outlet workflows, and same-day activity history.',
      },
      {
        icon: MapPin,
        title: 'GPS check-in & photo proof',
        description:
          'Location-validated visits with optional check-in photos, shift tracking, and automatic check-out safeguards.',
      },
      {
        icon: ClipboardList,
        title: 'Stock, price, sales & conversion',
        description:
          'Four core capture modules after every visit — opening and closing stock, own-brand vs competitor pricing, sales volumes, and outlet conversion counts.',
      },
      {
        icon: Users,
        title: 'Multi-company project campaigns',
        description:
          'Time-bound projects with dedicated product catalogs, outlet lists, teams, and reporting scoped per campaign or client.',
      },
      {
        icon: BarChart3,
        title: 'Executive dashboards',
        description:
          'Real-time charts for volume and value, team share, chain and SKU breakdowns, plus tabular data exports for deeper analysis.',
      },
      {
        icon: ShieldCheck,
        title: 'Governed master data',
        description:
          'Review unpublished field submissions, bulk publish or unpublish, Excel import, and configurable out-of-stock thresholds.',
      },
    ],
    audience:
      'Brand owners, distributors, and agencies running field sales or trade marketing programs — especially FMCG and retail audit operations in Malaysia.',
  },
  {
    id: 'score',
    name: 'SCORE',
    tagline: 'Survey · Contest · Registration',
    pitch:
      'An end-to-end consumer engagement platform for field marketing and retail campaigns — verified surveys, registration capture, digital incentives, and optional interactive experiences.',
    description:
      'SCORE goes beyond a simple form. It runs bilingual, mobile-first survey flows with SMS verification and participation codes; fulfills digital rewards; captures registration intent and trade-in leads; and pairs with custom engagement experiences for events and activations — all managed from a secure admin portal with rich analytics.',
    features: [
      {
        icon: ClipboardList,
        title: 'Multi-step survey flows',
        description:
          'Gated, bilingual journeys with eligibility screening, PDPA consent, conditional branching, and location-aware outlet lookup.',
      },
      {
        icon: ShieldCheck,
        title: 'Verified participation',
        description:
          'SMS OTP verification, unique participation codes, bot protection, and one-redemption-per-code controls to keep campaigns fair.',
      },
      {
        icon: Gift,
        title: 'Digital incentives',
        description:
          'Voucher inventory management, automatic reward assignment on completion, daily caps, and branded on-screen reward reveals.',
      },
      {
        icon: Users,
        title: 'Registration & lead capture',
        description:
          'Demographic profiles, device registration intent, care-team callbacks, and trade-in lead collection with exportable reports.',
      },
      {
        icon: BarChart3,
        title: 'Campaign analytics',
        description:
          'Date-range dashboards, redemption trends, consumer profiles, out-of-stock geography, and paginated data reports with Excel export.',
      },
      {
        icon: Gamepad2,
        title: 'Interactive engagement',
        description:
          'Custom branded experiences — timed challenges and touch-friendly activations for event booths, kiosks, and tablet deployments.',
      },
    ],
    audience:
      'Trade marketing teams, field sales operations, and campaign managers running consumer insight programs, incentive campaigns, and retail activations across Malaysia.',
  },
];
